import type {
  Disclaimer as LSGDisclaimer,
  ReferenceDisclaimerKind,
  ScopedDisclaimerManager,
} from '@volkswagen-onehub/disclaimer-manager';
import type {LayerHandleV2} from '@volkswagen-onehub/layer-manager';
import * as React from 'react';
import {DisclaimerLayerContent} from './disclaimer-layer-content';
import {CloseButton} from '../components/close-button';
import {useFeatureServices} from '../use-feature-services';
import {type TranslateFunction, useTranslate} from '../utils/translate';
import type {
  I18n,
  I18nKey,
} from '@volkswagen-onehub/car-technical-details-schemas';

export interface Disclaimer {
  readonly text: string;
  readonly reference: string;
  readonly onClick?: () => void;
}

function registerDisclaimer(
  scopedDisclaimerManager: ScopedDisclaimerManager,
  translate: TranslateFunction,
  disclaimerKind: ReferenceDisclaimerKind,
  key?: keyof I18n,
): {reference: string; text: string; disclaimer: LSGDisclaimer} | undefined {
  if (!key) {
    return undefined;
  }
  const text = translate(key);
  if (!text || text.length <= 0) {
    return undefined;
  }
  const reference = scopedDisclaimerManager.registerReferenceDisclaimer(
    disclaimerKind,
    text,
  );
  return {
    reference,
    text,
    disclaimer: {
      type: 'reference',
      refKind: disclaimerKind,
      reference: `${reference}.`,
      text,
    },
  };
}

export function useDisclaimer(key?: I18nKey): Disclaimer | undefined {
  const {
    'layer-manager': layerManager,
    'disclaimer-manager': disclaimerManager,
  } = useFeatureServices();

  const translate = useTranslate();

  const scopedDisclaimerManager = React.useMemo(
    () => disclaimerManager.getScopedDisclaimerManager('default'),
    [disclaimerManager],
  );

  const disclaimerKind: ReferenceDisclaimerKind = 'module-reference';

  const [disclaimer, setDisclaimer] = React.useState<Disclaimer | undefined>();

  React.useEffect(() => {
    const disclaimerData = registerDisclaimer(
      scopedDisclaimerManager,
      translate,
      disclaimerKind,
      key,
    );
    if (!disclaimerData) {
      return;
    }

    let layerHandle: LayerHandleV2<undefined> | undefined;

    const onClick = () => {
      if (layerHandle && !layerHandle.isClosed()) {
        layerHandle.close();
        layerHandle = undefined;
      } else {
        layerHandle = layerManager.openDisclaimerLayer(
          (_state, close) => (
            <DisclaimerLayerContent
              closeButton={
                <CloseButton
                  onClick={close}
                  ariaLabel={translate('layer.closebutton.aria.label')}
                />
              }
              disclaimer={disclaimerData.disclaimer}
            />
          ),
          undefined,
          {userCloseable: true},
        );
      }
    };

    setDisclaimer({
      text: disclaimerData.text,
      reference: disclaimerData.reference,
      onClick,
    });

    return () => {
      scopedDisclaimerManager.unregisterReferenceDisclaimer(
        disclaimerKind,
        disclaimerData.text,
      );
    };
  }, [key, layerManager, scopedDisclaimerManager, translate]);

  if (typeof window == 'undefined') {
    const disclaimerData = registerDisclaimer(
      scopedDisclaimerManager,
      translate,
      disclaimerKind,
      key,
    );
    if (!disclaimerData) {
      return undefined;
    }
    return {text: disclaimerData.text, reference: disclaimerData.reference};
  }

  return disclaimer;
}
